import { useEffect, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api, ApiError } from '../api/client';
import { IconCancel, IconCreate } from '../icons/HvacIcons';
import './hv-modal.css';

export interface AddedLeaf {
  id: string;
  deviceId: string;
  parentNodeId: string;
  symbol: string;
  displayName: string | null;
  valueType: string;
  metricKind: string;
  unit: string | null;
  dimension: string | null;
  minValue: number | null;
  maxValue: number | null;
  decimals: number | null;
  warnLow: number | null;
  warnHigh: number | null;
  alarmLow: number | null;
  alarmHigh: number | null;
  writability: string;
  readSensitivity: string;
  writeSensitivity: string;
  safetyMarker: boolean;
  forceAllowed: boolean;
  escalateToAlarm: boolean;
  alarmRole: string;
  sortOrder: number;
}

interface Props {
  plantId: string;
  parent: { parentNodeId: string; parentLabel: string };
  onCreated(leaf: AddedLeaf): void;
  onClose(): void;
}

const VALUE_TYPES = ['Float', 'Int', 'Bool', 'Enum', 'Flags', 'String'];
const WRITABILITY = ['ReadOnly', 'WriteOnly', 'ReadWrite'];

/** POST /api/plants/{plantId}/leaves. Symbol + ValueType are locked after
 *  creation, everything else is editable later via LeafConfigModal. */
export function AddLeafModal({ plantId, parent, onCreated, onClose }: Props) {
  const [symbol, setSymbol] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [valueType, setValueType] = useState('Float');
  const [unit, setUnit] = useState('');
  const [dimension, setDimension] = useState('');
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [decimals, setDecimals] = useState('');
  const [warnLow, setWarnLow] = useState('');
  const [warnHigh, setWarnHigh] = useState('');
  const [alarmLow, setAlarmLow] = useState('');
  const [alarmHigh, setAlarmHigh] = useState('');
  const [writability, setWritability] = useState('ReadOnly');
  const [sortOrder, setSortOrder] = useState('10');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const parseNum = (raw: string, name: string): number | null => {
    const t = raw.trim();
    if (t === '') return null;
    const n = Number(t);
    if (!Number.isFinite(n)) throw new Error(`${name} muss eine gültige Zahl sein.`);
    return n;
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const sym = symbol.trim();
      if (sym === '') throw new Error('Symbol darf nicht leer sein.');
      const so = Number(sortOrder);
      if (!Number.isFinite(so) || !Number.isInteger(so)) throw new Error('Sortierung muss eine ganze Zahl sein.');
      const body: Record<string, unknown> = {
        parentNodeId: parent.parentNodeId,
        symbol: sym,
        valueType,
        writability,
        sortOrder: so,
      };
      const dn = displayName.trim();
      if (dn !== '') body.displayName = dn;
      const u = unit.trim();
      if (u !== '') body.unit = u;
      const d = dimension.trim();
      if (d !== '') body.dimension = d;
      const min = parseNum(minValue, 'Min');
      if (min !== null) body.minValue = min;
      const max = parseNum(maxValue, 'Max');
      if (max !== null) body.maxValue = max;
      const dec = parseNum(decimals, 'Decimals');
      if (dec !== null) body.decimals = dec;
      const wl = parseNum(warnLow, 'WarnLow');
      if (wl !== null) body.warnLow = wl;
      const wh = parseNum(warnHigh, 'WarnHigh');
      if (wh !== null) body.warnHigh = wh;
      const al = parseNum(alarmLow, 'AlarmLow');
      if (al !== null) body.alarmLow = al;
      const ah = parseNum(alarmHigh, 'AlarmHigh');
      if (ah !== null) body.alarmHigh = ah;
      const res = await api.post<AddedLeaf>(`/api/plants/${plantId}/leaves`, body);
      onCreated(res);
    } catch (e) {
      if (e instanceof ApiError) {
        const bodyMsg = (e.body as { error?: string })?.error;
        setError(bodyMsg ?? `Anlegen fehlgeschlagen (${e.status}).`);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('Unbekannter Fehler.');
      }
      setSaving(false);
    }
  };

  return createPortal(
    <div class="hv-modal-backdrop" onClick={onClose}>
      <div class="hv-modal" onClick={(e) => e.stopPropagation()} style={{ width: 'min(680px, 92vw)' }}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Neuer Datenpunkt</h2>
          <button type="button" class="hv-modal__close" onClick={onClose} aria-label="Schließen">×</button>
        </div>
        <div class="hv-modal__body">
          <div class="hv-modal__field">
            <label>Übergeordneter Node</label>
            <input type="text" value={parent.parentLabel} disabled />
          </div>
          <div class="hv-modal__row">
            <div class="hv-modal__field">
              <label>Symbol *</label>
              <input type="text" value={symbol} onInput={(e) => setSymbol((e.target as HTMLInputElement).value)} placeholder="z.B. t_vorlauf" />
            </div>
            <div class="hv-modal__field">
              <label>ValueType *</label>
              <select value={valueType} onChange={(e) => setValueType((e.target as HTMLSelectElement).value)}>
                {VALUE_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>
          <div class="hv-modal__field">
            <label>Anzeigename</label>
            <input type="text" value={displayName} onInput={(e) => setDisplayName((e.target as HTMLInputElement).value)} placeholder="optional" />
          </div>
          <div class="hv-modal__row">
            <div class="hv-modal__field">
              <label>Unit</label>
              <input type="text" value={unit} onInput={(e) => setUnit((e.target as HTMLInputElement).value)} placeholder="z.B. °C" />
            </div>
            <div class="hv-modal__field">
              <label>Dimension</label>
              <input type="text" value={dimension} onInput={(e) => setDimension((e.target as HTMLInputElement).value)} placeholder="z.B. temperature" />
            </div>
            <div class="hv-modal__field">
              <label>Decimals</label>
              <input type="text" value={decimals} onInput={(e) => setDecimals((e.target as HTMLInputElement).value)} placeholder="0-3" />
            </div>
          </div>
          <div class="hv-modal__row">
            <div class="hv-modal__field">
              <label>Min</label>
              <input type="text" value={minValue} onInput={(e) => setMinValue((e.target as HTMLInputElement).value)} />
            </div>
            <div class="hv-modal__field">
              <label>Max</label>
              <input type="text" value={maxValue} onInput={(e) => setMaxValue((e.target as HTMLInputElement).value)} />
            </div>
          </div>
          <div class="hv-modal__row">
            <div class="hv-modal__field">
              <label>Warn Low</label>
              <input type="text" value={warnLow} onInput={(e) => setWarnLow((e.target as HTMLInputElement).value)} />
            </div>
            <div class="hv-modal__field">
              <label>Warn High</label>
              <input type="text" value={warnHigh} onInput={(e) => setWarnHigh((e.target as HTMLInputElement).value)} />
            </div>
            <div class="hv-modal__field">
              <label>Alarm Low</label>
              <input type="text" value={alarmLow} onInput={(e) => setAlarmLow((e.target as HTMLInputElement).value)} />
            </div>
            <div class="hv-modal__field">
              <label>Alarm High</label>
              <input type="text" value={alarmHigh} onInput={(e) => setAlarmHigh((e.target as HTMLInputElement).value)} />
            </div>
          </div>
          <div class="hv-modal__row">
            <div class="hv-modal__field">
              <label>Writability</label>
              <select value={writability} onChange={(e) => setWritability((e.target as HTMLSelectElement).value)}>
                {WRITABILITY.map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
            <div class="hv-modal__field">
              <label>Sortierung</label>
              <input type="text" value={sortOrder} onInput={(e) => setSortOrder((e.target as HTMLInputElement).value)} />
            </div>
          </div>
          {error && <p class="hv-modal__error">{error}</p>}
        </div>
        <div class="hv-modal__footer">
          <div style={{ flex: 1 }} />
          <button type="button" class="hv-btn" onClick={onClose} disabled={saving}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCancel size={14} />Abbrechen</span>
          </button>
          <button type="button" class="hv-btn hv-btn--primary" onClick={save} disabled={saving}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCreate size={14} />Anlegen</span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
